import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Payment, Refresh, Warning, ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { fetchPaymentMethods, createPaymentMethod, updatePaymentMethod, deletePaymentMethod, clearError } from '../../store/slices/paymentMethodSlice';
import FileUpload from '../../components/common/FileUpload';
import { uploadFile as uploadFileService } from '../../services/upload';

const defaultForm = {
  name: '',
  logo: '',
  visible_on_deposit: true,
  visible_on_withdraw: true,
  precedence: 99
};

const PaymentMethod = () => {
  const { t } = useTranslation(['paymentMethods', 'common']);
  const dispatch = useDispatch();
  const { paymentMethods, loading, error, totalPaymentMethods } = useSelector((state) => state.paymentMethod || { paymentMethods: [], loading: false, error: null, totalPaymentMethods: 0 });
  
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, paymentMethodId: null, paymentMethodName: '' });
  
  // Image preview modal state
  const [imagePreview, setImagePreview] = useState({ open: false, src: '', alt: '' });
  
  // Filter states for dynamic search
  const [filters, setFilters] = useState({
    name: '',
    visible_on_deposit: '',
    visible_on_withdraw: ''
  });

  // Custom debounce hook
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      
      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);
    
    return debouncedValue;
  };

  // Individual debounced values to avoid object recreation
  const debouncedName = useDebounce(filters.name, 500);
  const debouncedDeposit = useDebounce(filters.visible_on_deposit, 500);
  const debouncedWithdraw = useDebounce(filters.visible_on_withdraw, 500);

  // Pagination state
  const [page, setPage] = useState(0); // 0-based for frontend, 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Calculate total pages from totalPaymentMethods
  const totalPages = Math.ceil(totalPaymentMethods / rowsPerPage) || 1;

  // Fetch payment methods with filters
  const fetchPaymentMethodsData = useCallback((pageArg, rowsArg, filterParams = {}) => {
    // Only include non-empty filters
    const cleanFilters = {};
    Object.entries(filterParams).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        cleanFilters[key] = value;
      }
    });

    dispatch(fetchPaymentMethods({
      ...cleanFilters,
      page: pageArg + 1, // backend expects 1-based
      page_size: rowsArg,
      ordering: '-created_at',
    }));
  }, [dispatch]);

  // Auto-apply filters when debounced values change
  useEffect(() => {
    const debouncedFilters = {
      name: debouncedName,
      visible_on_deposit: debouncedDeposit,
      visible_on_withdraw: debouncedWithdraw
    };
    setPage(0); // Reset to first page when filters change
    fetchPaymentMethodsData(0, rowsPerPage, debouncedFilters);
  }, [debouncedName, debouncedDeposit, debouncedWithdraw, rowsPerPage, fetchPaymentMethodsData]);

  // Handle pagination changes (only when page > 0 to avoid double fetch)
  useEffect(() => {
    if (page > 0) {
      const debouncedFilters = {
        name: debouncedName,
        visible_on_deposit: debouncedDeposit,
        visible_on_withdraw: debouncedWithdraw
      };
      fetchPaymentMethodsData(page, rowsPerPage, debouncedFilters);
    }
  }, [page, fetchPaymentMethodsData, rowsPerPage, debouncedName, debouncedDeposit, debouncedWithdraw]);

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Refresh function with current filters
  const handleRefresh = () => {
    const debouncedFilters = {
      name: debouncedName,
      visible_on_deposit: debouncedDeposit,
      visible_on_withdraw: debouncedWithdraw
    };
    setPage(0);
    fetchPaymentMethodsData(0, rowsPerPage, debouncedFilters);
  };

  // Filter change handler
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      name: '',
      visible_on_deposit: '',
      visible_on_withdraw: ''
    });
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(filter => filter && filter.trim() !== '');

  useEffect(() => {
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // FILTERING STATE REMOVED

  const handleOpen = (row) => {
    if (row) {
      setEditId(row.id);
      setForm({ 
        name: row.name || '',
        logo: row.logo || '',
        visible_on_deposit: Boolean(row.visible_on_deposit),
        visible_on_withdraw: Boolean(row.visible_on_withdraw),
        precedence: row.precedence !== undefined ? row.precedence : 99
      });
    } else {
      setEditId(null);
      setForm(defaultForm);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditId(null);
    setUploadedFile(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let finalValue = value;
    if (type === 'checkbox') {
      finalValue = checked;
    } else if (type === 'number') {
      // Handle number inputs - convert to number, default to 99 if empty
      finalValue = value === '' ? 99 : Number(value);
    }
    
    setForm({ 
      ...form, 
      [name]: finalValue
    });
  };

  const handleSave = async () => {
    if (!form.name) {
      setSnackbar({ open: true, message: t('messages.nameRequired'), severity: 'error' });
      return;
    }

    try {
      let formData = { ...form };
      
      // If a file is uploaded, upload it first and get the file URL
      if (uploadedFile) {
        const fileUrl = await uploadFile(uploadedFile);
        formData.logo = fileUrl;
      }

      if (editId) {
        const result = await dispatch(updatePaymentMethod({ id: editId, paymentMethodData: formData })).unwrap();
        setSnackbar({ open: true, message: t('messages.updatedSuccessfully'), severity: 'success' });
        // Refresh the payment methods list to get updated data
        const currentFilters = {
          name: debouncedName,
          visible_on_deposit: debouncedDeposit,
          visible_on_withdraw: debouncedWithdraw
        };
        fetchPaymentMethodsData(page, rowsPerPage, currentFilters);
      } else {
        const result = await dispatch(createPaymentMethod(formData)).unwrap();
        setSnackbar({ open: true, message: t('messages.addedSuccessfully'), severity: 'success' });
        // Refresh the payment methods list to get new data
        const currentFilters = {
          name: debouncedName,
          visible_on_deposit: debouncedDeposit,
          visible_on_withdraw: debouncedWithdraw
        };
        fetchPaymentMethodsData(page, rowsPerPage, currentFilters);
      }
      handleClose();
    } catch (error) {
      console.error('Error saving payment method:', error);
      setSnackbar({ open: true, message: error, severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deletePaymentMethod(id)).unwrap();
      setSnackbar({ open: true, message: t('messages.deletedSuccessfully'), severity: 'info' });
      setDeleteModal({ open: false, paymentMethodId: null, paymentMethodName: '' });
      // Refresh the payment methods list after deletion
      const currentFilters = {
        name: debouncedName,
        visible_on_deposit: debouncedDeposit,
        visible_on_withdraw: debouncedWithdraw
      };
      fetchPaymentMethodsData(page, rowsPerPage, currentFilters);
    } catch (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
    }
  };

  // New function to handle delete confirmation
  const handleDeleteClick = (paymentMethod) => {
    setDeleteModal({
      open: true,
      paymentMethodId: paymentMethod.id,
      paymentMethodName: paymentMethod.name
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, paymentMethodId: null, paymentMethodName: '' });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.paymentMethodId) {
      handleDelete(deleteModal.paymentMethodId);
    }
  };

  const handleFileSelect = (file) => {
    setUploadedFile(file);
    // Clear the logo URL when a new file is selected
    setForm(prev => ({ ...prev, logo: '' }));
  };

  const handleFileRemove = () => {
    setUploadedFile(null);
  };

  const handleImageClick = (src, alt) => {
    setImagePreview({ open: true, src, alt });
  };

  const handleImagePreviewClose = () => {
    setImagePreview({ open: false, src: '', alt: '' });
  };

  const uploadFile = async (file) => {
    try {
      const result = await uploadFileService(file);
      return result.file_url;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error(error.message || 'Failed to upload file');
    }
  };

  const getVisibilityColor = (visible) => {
    return visible ? '#4CAF50' : '#F44336';
  };

  // Add serial numbers to payment methods for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));


  const columns = [
    { field: 'sn', headerName: t('table.sn'), width: 80, sortable: false, filterable: false },
    { 
      field: 'name', 
      headerName: t('table.paymentMethod'), 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => {
        const name = params.row?.name;
        return <span>{name || 'N/A'}</span>;
      }
    },
    { 
      field: 'logo', 
      headerName: t('table.logo'), 
      flex: 1, 
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const logo =  params.row?.logo || params.row?.logo_url;
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            p: 1
          }}>
            {logo ? (
              <Box sx={{ 
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img 
                  src={logo} 
                  alt={`${params.row?.name} logo`}
                  style={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: 8,
                    objectFit: 'contain',
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                  }}
                  onClick={() => handleImageClick(logo, `${params.row?.name} logo`)}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <Box sx={{
                  display: 'none',
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#f5f5f5',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: '#999',
                  textAlign: 'center'
                }}>
                  {t('messages.noImage')}
                </Box>
              </Box>
            ) : (
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: 8,
                border: '1px solid #e0e0e0',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                color: '#999',
                textAlign: 'center'
              }}>
                {t('messages.noLogo')}
              </Box>
            )}
          </Box>
        );
      }
    },
    { 
      field: 'visible_on_deposit', 
      headerName: t('table.visibleOnDeposit'), 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => {
        const visible = params.row?.visible_on_deposit;
        return (
          <Chip
            label={visible ? t('status.yes') : t('status.no')}
            size="small"
            sx={{
              backgroundColor: getVisibilityColor(visible),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    { 
      field: 'visible_on_withdraw', 
      headerName: t('table.visibleOnWithdraw'), 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => {
        const visible = params.row?.visible_on_withdraw;
        return (
          <Chip
            label={visible ? t('status.yes') : t('status.no')}
            size="small"
            sx={{
              backgroundColor: getVisibilityColor(visible),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    { 
      field: 'precedence', 
      headerName: t('table.precedence'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const precedence = params.row?.precedence;
        return (
          <Chip
            label={precedence !== undefined ? precedence : 99}
            size="small"
            sx={{
              backgroundColor: precedence === 0 ? '#4CAF50' : precedence < 50 ? '#FF9800' : '#9E9E9E',
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    { 
      field: 'created_at', 
      headerName: t('table.createdDate'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const createdAt = params.row?.created_at;
        if (!createdAt) return <span>N/A</span>;
        
        try {
          const date = new Date(createdAt);
          if (isNaN(date.getTime())) {
            return <span>N/A</span>;
          }
          return <span>{date.toLocaleDateString()}</span>;
        } catch (error) {
          console.error('Error formatting date:', error, createdAt);
          return <span>N/A</span>;
        }
      }
    },
    {
      field: 'actions',
      headerName: t('table.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <Box>
            <Tooltip title={t('tooltips.edit')}>
              <IconButton color="primary" onClick={() => handleOpen(params.row)}>
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('tooltips.delete')}>
              <IconButton color="error" onClick={() => handleDeleteClick(params.row)}>
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  if (loading && paymentMethods.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('messages.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={() => handleOpen()}
          >
            {t('buttons.addPaymentMethod')}
          </Button>
        </Box>
      </Box>
      
      {/* Dynamic Filter Section */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold' }}>
                {t('filters.title')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t('filters.description')}
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flexWrap: 'wrap'
            }}>
              <Typography variant="body2" color="textSecondary">
                {t('filters.showing', { count: paymentMethods.length })}
                {hasActiveFilters && (
                  <span>{t('filters.filtered')}</span>
                )}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                sx={{ borderColor: '#DC143C', color: '#DC143C' }}
              >
                {t('filters.clearFilters')}
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('filters.searchName')}
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                size="small"
                fullWidth
                placeholder={t('filters.searchPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('filters.visibleOnDeposit')}
                value={filters.visible_on_deposit}
                onChange={(e) => handleFilterChange('visible_on_deposit', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">{t('filters.all')}</MenuItem>
                <MenuItem value="true">{t('filters.yes')}</MenuItem>
                <MenuItem value="false">{t('filters.no')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('filters.visibleOnWithdraw')}
                value={filters.visible_on_withdraw}
                onChange={(e) => handleFilterChange('visible_on_withdraw', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">{t('filters.all')}</MenuItem>
                <MenuItem value="true">{t('filters.yes')}</MenuItem>
                <MenuItem value="false">{t('filters.no')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Payment Methods Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {paymentMethods.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('messages.noPaymentMethods')}
                </Typography>
                <Button variant="outlined" onClick={handleRefresh}>
                  {t('buttons.refreshData')}
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(paymentMethods, page, rowsPerPage)}
                columns={columns}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1200, // Ensure minimum width for all columns
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('pagination.page', { current: totalPages === 0 ? 0 : page + 1, total: totalPages })}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {editId ? t('dialogs.edit.title') : t('dialogs.add.title')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField 
              label={editId ? t('dialogs.edit.nameLabel') : t('dialogs.add.nameLabel')} 
              name="name" 
              value={form.name} 
              onChange={handleChange} 
              required 
              fullWidth 
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {editId ? t('dialogs.edit.logoUpload') : t('dialogs.add.logoUpload')}
              </Typography>
              <FileUpload
                onFileSelect={handleFileSelect}
                onFileRemove={handleFileRemove}
                value={uploadedFile}
                acceptedTypes={['image/*']}
                maxSize={2 * 1024 * 1024} // 2MB
                label={editId ? t('dialogs.edit.uploadLogo') : t('dialogs.add.uploadLogo')}
                helperText={editId ? t('dialogs.edit.uploadHelper') : t('dialogs.add.uploadHelper')}
              />
              {form.logo && !uploadedFile && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {editId ? t('dialogs.edit.currentLogoUrl') : t('dialogs.add.currentLogoUrl')} {form.logo}
                  </Typography>
                </Box>
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={form.visible_on_deposit}
                  onChange={handleChange}
                  name="visible_on_deposit"
                  color="primary"
                />
              }
              label={editId ? t('dialogs.edit.visibleOnDeposit') : t('dialogs.add.visibleOnDeposit')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.visible_on_withdraw}
                  onChange={handleChange}
                  name="visible_on_withdraw"
                  color="primary"
                />
              }
              label={editId ? t('dialogs.edit.visibleOnWithdraw') : t('dialogs.add.visibleOnWithdraw')}
            />
            <TextField 
              label={editId ? t('dialogs.edit.precedence') : t('dialogs.add.precedence')} 
              name="precedence" 
              type="number"
              value={form.precedence} 
              onChange={handleChange} 
              fullWidth 
              inputProps={{ min: 0, max: 999 }}
              helperText={t('dialogs.precedenceHelper')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('buttons.cancel')}</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {editId ? t('buttons.update') : t('buttons.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteModal.open} 
        onClose={handleDeleteCancel}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          color: '#DC143C', 
          fontWeight: 'bold',
          pb: 1
        }}>
          <Warning sx={{ color: '#DC143C', fontSize: 28 }} />
          {t('dialogs.delete.title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#333' }}>
            <span dangerouslySetInnerHTML={{ __html: t('dialogs.delete.message', { name: deleteModal.paymentMethodName }) }} />
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ 
            backgroundColor: '#FFF3E0', 
            p: 2, 
            borderRadius: 1, 
            border: '1px solid #FFB74D',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Warning sx={{ color: '#F57C00', fontSize: 20 }} />
            {t('dialogs.delete.warning')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ 
              borderColor: '#666', 
              color: '#666',
              '&:hover': { 
                borderColor: '#333', 
                backgroundColor: '#f5f5f5' 
              }
            }}
          >
            {t('buttons.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ 
              backgroundColor: '#DC143C', 
              '&:hover': { backgroundColor: '#B91C3C' },
              px: 3
            }}
            startIcon={<Delete />}
          >
            {t('buttons.deletePaymentMethod')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Preview Modal */}
      <Dialog 
        open={imagePreview.open} 
        onClose={handleImagePreviewClose}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            backgroundColor: '#fff'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
            {t('dialogs.imagePreview.title')}
          </Typography>
          <IconButton onClick={handleImagePreviewClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          {imagePreview.src && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: 300
            }}>
              <img 
                src={imagePreview.src} 
                alt={imagePreview.alt}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '500px', 
                  borderRadius: 8,
                  objectFit: 'contain',
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#f5f5f5'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <Box sx={{
                display: 'none',
                width: 200,
                height: 200,
                borderRadius: 8,
                border: '1px solid #e0e0e0',
                backgroundColor: '#f5f5f5',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                color: '#999',
                textAlign: 'center'
              }}>
                {t('messages.failedToLoadImage')}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PaymentMethod;
